// datemath.ts
import { parseISO, add, sub, startOfYear, startOfMonth, startOfDay, startOfHour, startOfMinute, startOfSecond } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import type { Duration } from 'date-fns';

export interface ParseOptions {
  date?: Date | number;
  roundUp: boolean;
  timeZone?: string; // IANA name, e.g. "America/New_York"
  useTimeZoneForRounding?: boolean;
  separator?: string;
}

interface EvaluateOptions extends ParseOptions {
  date: Date;
}

export const nowWithin = (fromExpr: string, toExpr: string) => {
  const now = new Date();
  const from = parse(fromExpr, { date: now, roundUp: false });
  const to = parse(toExpr, { date: now, roundUp: true });
  return now >= from && now <= to;
};

export const nowWithinAndShortRange = (fromExpr: string, toExpr: string, maxRangeMs: number) => {
  const now = new Date();
  const from = parse(fromExpr, { date: now, roundUp: false });
  const to = parse(toExpr, { date: now, roundUp: true });
  return now >= from && now <= to && (to.getTime() - from.getTime()) <= maxRangeMs;
}

export default function parse(expr: string, { date, roundUp=false, timeZone, useTimeZoneForRounding, separator }: ParseOptions): Date {
  let math = '';
  let base: Date;

  expr = (expr || '').trim();
  if (expr.startsWith('now')) {
    base = new Date(date ?? Date.now());
    math = expr.slice(3);
    const sepIdx = math.indexOf(separator || '||');
    math = sepIdx === -1 ? math : math.slice(sepIdx + 2);
  } else {
    const sepIdx = expr.indexOf(separator || '||');
    const timeStr = sepIdx === -1 ? expr : expr.slice(0, sepIdx);
    base = parseTime(timeStr, timeZone);
    math = sepIdx === -1 ? '' : expr.slice(sepIdx + 2);
  }

  if (!math) return base;
  return evaluate(math, { date: base, roundUp, timeZone, useTimeZoneForRounding });
}

function evaluate(expr: string, { date, roundUp=false, timeZone, useTimeZoneForRounding }: EvaluateOptions): Date {
  let i = 0;
  let val = 0;
  let d = date;

  while (i < expr.length) {
    const ch = expr[i++];
    if (ch === ' ') {
      // skip
    } else if (ch === '+' || ch === '-' || ch === 'P') {
      val = 0;
      const sign = ch === '+' || ch === 'P' ? 1 : -1;
      while (/[0-9]/.test(expr[i])) val = val * 10 + Number(expr[i++]);
      val = val || 1;
      const unit = expr[i++];
      d = shift(d, sign * val, unit);
    } else if (ch === '/') {
      const unit = expr[i++];
      d = round(d, unit, roundUp, timeZone, useTimeZoneForRounding);
    } else {
      throw new Error(`Unexpected token '${ch}' in date math '${expr}'`);
    }
  }

  return d;
}

function shift(date: Date, amount: number, unit: string): Date {
  switch (unit) {
    case 'y': return add(date, { years: amount });
    case 'M': return add(date, { months: amount });
    case 'd': return add(date, { days: amount });
    case 'w': return add(date, { weeks: amount });
    case 'h': return add(date, { hours: amount });
    case 'm': return add(date, { minutes: amount });
    case 's': return add(date, { seconds: amount });
    default: throw new Error(`Unknown unit '${unit}'`);
  }
}

function round(date: Date, unit: string, roundUp: boolean, timeZone?: string, useTZ?: boolean): Date {
  date = useTZ && timeZone ? toZonedTime(date, timeZone) : date;

  switch (unit) {
    case 'y': date = startOfYear(date); break;
    case 'M': date = startOfMonth(date); break;
    case 'd': date = startOfDay(date); break;
    case 'h': date = startOfHour(date); break;
    case 'm': date = startOfMinute(date); break;
    case 's': date = startOfSecond(date); break;
    default: throw new Error(`Unknown rounding unit '${unit}'`);
  }

  if (roundUp) date = sub(add(date, { [unitName(unit)]: 1 }), { seconds: 1 / 1000 });
  return useTZ && timeZone ? fromZonedTime(date, timeZone) : date;
}

const UNIT_NAMES: Record<string, keyof Duration> = { y: "years", M: "months", d: "days", h: "hours", m: "minutes", s: "seconds" } as const;
function unitName(u: keyof typeof UNIT_NAMES) {
  return UNIT_NAMES[u];
}

function parseTime(s: string, timeZone?: string): Date {
  if (/^\d+$/.test(s)) return new Date(Number(s.length === 10 ? s + "000" : s));
  const parsed = parseISO(s);
  if (timeZone && !/Z|[+-]\d\d:?\d\d$/.test(s))
    return fromZonedTime(parsed, timeZone);
  return parsed;
}
